import React from 'react';
import { useParams } from 'react-router';
import { Phone2Icon } from '@newcore/icons';
import { Button } from '@newcore/ui/buttons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@newcore/ui/dialog';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import {
  PairingActiveState,
  PairingErrorState,
  PairingExpiredState,
  PairingIntroState,
  PairingLoadingState,
  PairingSuccessState,
} from '@/components/device-pairing/states';
import { useDevicePairing } from '@/components/device-pairing/use-device-pairing';
import { DevicePairingStatus } from '@/enums/device-pairing-status.enums';
import { buildPairingQrValue } from '@/utils/pairing-utils';

interface DevicePairingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  cancelButtonText?: string;
  /** If true, uses onboarding endpoints (session cookie). If false, uses enrollment endpoints (authenticated user session). */
  isOnboarding?: boolean;
  /** If true, shows intro screen first before pairing. Used at end of onboarding flow. */
  showIntro?: boolean;
  /** Tenant slug used to build the QR pairing URL. Falls back to the `:tenant` route param. */
  tenantSlug?: string;
}

/**
 * Dialog for adding a new device via QR code pairing.
 *
 * Used in two contexts:
 * - Onboarding flow (isOnboarding=true): Uses onboarding session cookie for auth
 * - Enrollment flow (isOnboarding=false): Uses authenticated user session for auth
 */
export const DevicePairingDialog: React.FC<DevicePairingDialogProps> = ({
  isOpen,
  onClose,
  cancelButtonText = 'Cancel',
  isOnboarding = false,
  showIntro = false,
  tenantSlug,
}) => {
  const { tenant: tenantFromRoute } = useParams<{ tenant: string }>();
  const tenant = tenantSlug ?? tenantFromRoute;
  const { status, error, pairingData, handleRetry, handleSetupApp } =
    useDevicePairing({ isOpen, onClose, isOnboarding, showIntro });

  const isIntroStatus = status === DevicePairingStatus.INTRO;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-md'>
        {/* Intro state has its own header layout, hide dialog header */}
        {!isIntroStatus && (
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Phone2Icon className='h-5 w-5 text-primary' />
              Add New Device
            </DialogTitle>
            <DialogDescription>Scan with your mobile device</DialogDescription>
          </DialogHeader>
        )}

        <div className='space-y-4'>
          {status === DevicePairingStatus.INTRO && (
            <PairingIntroState onSetupApp={handleSetupApp} onSkip={onClose} />
          )}

          {(status === DevicePairingStatus.IDLE ||
            status === DevicePairingStatus.STARTING) && <PairingLoadingState />}

          {status === DevicePairingStatus.ERROR && (
            <PairingErrorState error={error} onRetry={handleRetry} />
          )}

          {status === DevicePairingStatus.EXPIRED && (
            <PairingExpiredState onRetry={handleRetry} />
          )}

          {status === DevicePairingStatus.SUCCESS && <PairingSuccessState />}

          {status === DevicePairingStatus.ACTIVE && pairingData && (
            <PairingActiveState
              pairingData={pairingData}
              qrValue={buildPairingQrValue(pairingData.pairingCode, tenant)}
            />
          )}
        </div>

        {/* Only show footer with cancel button when not in intro state (intro has its own buttons) */}
        {!isIntroStatus && (
          <DialogFooter>
            <Button variant={ButtonVariants.SECONDARY} onClick={onClose}>
              {cancelButtonText}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
